// student-frontend/src/modules/center-services/AccessibleService.ts
import { apiClient } from "infra/api-client";
import { BASE_URL } from "global";
import moment, { Moment } from "moment";
import pDefer, { DeferredPromise } from "p-defer";
import { getUserSession } from "common/session/user-session";

type CenterServiceDto = {
    service_type: string;
    effective_date: string;
    end_date: string;
};

type CenterService = {
    service_type: string;
    effective_date: Moment;
    end_date: Moment;
};

const ServiceType = {
    GAMES: "games",
    ENRICHMENT: "enrichment",
    ONLINE_SESSION: "video_conferencing",
} as const;

// New: bundle token that should satisfy both GAMES and ENRICHMENT checks
const BUNDLE_TOKEN = "games_enrichment";

// Expand legacy/variant labels so enrichment is detected reliably
const NORMALIZE: Record<string, keyof typeof ServiceType> = {
    games: "GAMES",
    enrichment: "ENRICHMENT",
    lll: "ENRICHMENT",
    "lll_educational": "ENRICHMENT",
    "lll-educational": "ENRICHMENT",
    "lll educational": "ENRICHMENT",
    video_conferencing: "ONLINE_SESSION",
    // Note: bundle handled separately in the matcher (covers both games & enrichment)
};

function toCanonicalKey(
    s?: string | null
): keyof typeof ServiceType | undefined {
    if (!s) return;
    return NORMALIZE[String(s).trim().toLowerCase()];
}

// Accept common bundle spellings: "games_enrichment", "games enrichment", "games+enrichment", "games-enrichment"
function isBundle(raw: string): boolean {
    const squashed = raw.toLowerCase().replace(/[^a-z]/g, "");
    return squashed === "gamesenrichment";
}

export class AccessibleService {
    private static cacheMs = 10 * 60 * 1000;
    private services: CenterService[] = [];
    private defer: DeferredPromise<void> | null = null;
    private cacheTimer: ReturnType<typeof setTimeout> | null = null;

    async init() {
        if (!this.defer) {
            this.defer = pDefer<void>();
            this.fetch().then();
        }
        return this.defer.promise;
    }

    reset() {
        // Clear cached services and any pending timer; next init() will refetch.
        this.services = [];
        this.defer = null;
        if (this.cacheTimer) {
            clearTimeout(this.cacheTimer);
            this.cacheTimer = null;
        }
    }

    private async fetch() {
        try {
            const { studentKey } = getUserSession() || {};
            if (!studentKey) {
                this.services = [];
                this.defer?.resolve();
                return;
            }

            const resp = await apiClient.post(
                BASE_URL + "centerServices/get-accessible-services-by-student",
                { pk_student_key: studentKey, active_only: true }
            );

            const payload = resp?.data;
            const arr: CenterServiceDto[] = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                    ? payload
                    : [];

            if (!Array.isArray(arr)) {
                console.warn(
                    "centerServices/get-accessible-services-by-student: unexpected payload",
                    payload
                );
            }

            // Strict ISO parsing; drop any rows with invalid/missing dates
            this.services = (arr || [])
                .map((e) => ({
                    service_type: e.service_type,
                    effective_date: moment(e.effective_date, moment.ISO_8601, true),
                    end_date: moment(e.end_date, moment.ISO_8601, true),
                }))
                .filter((s) => s.effective_date.isValid() && s.end_date.isValid());

            this.defer?.resolve();
        } finally {
            // Drop the "in-flight" handle after cacheMs so a fresh .init() refetches
            this.cacheTimer = setTimeout(() => {
                this.defer = null;
                this.cacheTimer = null;
            }, AccessibleService.cacheMs);
        }
    }

    hasAccessToGames() {
        return this.has(ServiceType.GAMES);
    }

    hasAccessToEnrichment() {
        return this.has(ServiceType.ENRICHMENT);
    }

    // Alias for code that still calls the old name
    hasAccessToLll() {
        return this.has(ServiceType.ENRICHMENT);
    }

    hasAccessToOnlineSession() {
        return this.has(ServiceType.ONLINE_SESSION);
    }


    private has(target: (typeof ServiceType)[keyof typeof ServiceType]) {
        const now = moment();
        return !!this.services.find((row) => {
            const raw = String(row.service_type || "").trim().toLowerCase();

            if (isBundle(raw)) {
                const bundleCovers =
                    target === ServiceType.GAMES || target === ServiceType.ENRICHMENT;
                if (!bundleCovers) return false;
            } else {
                const key = toCanonicalKey(raw);
                if (!key) return false;
                if (ServiceType[key] !== target) return false;
            }

            return (
                row.effective_date.isSameOrBefore(now, "day") &&
                row.end_date.isSameOrAfter(now, "day")
            );
        });
    }
}

export const accessibleService = new AccessibleService();
